// src/components/common/PushForeground.jsx
//
// Pushes that arrive while MintLink is open in the foreground.
//
// FCM deliberately does NOT show a system notification in that case — it hands
// the payload to onMessage and expects the page to decide. There was no
// onMessage handler at all, so any push landing while someone had the app open
// was silently dropped. Given creators sit on the dashboard waiting for sales,
// that was probably the most-missed notification of the lot.
//
// This is also the one place a custom sound is genuinely possible. A
// background push cannot have one — the Web Notifications `sound` property was
// never implemented by any browser and has been dropped from the spec, so
// Android, iOS and desktop all play the OS default and offer no override. In
// the foreground the page is running, so it can just play audio.
//
// The chime is synthesised with WebAudio rather than shipped as an mp3: no
// binary asset, no extra request, no cache to bust, and it can't fail to load.
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getMessagingIfSupported } from '../../firebase/config';
import { apiService } from '../../services/api';

/** Report that this push actually reached a device (or was opened).
 *  Fire-and-forget: telemetry must never break the notification it measures. */
const ackPush = (pushId, event) => {
  if (!pushId) return;
  apiService.ackPush(pushId, event).catch(() => {});
};

/** Two quick rising notes — present enough to notice, short enough not to grate. */
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    // Browsers suspend audio until a user gesture has happened. If this is a
    // fresh tab nobody has touched, skip rather than throw.
    if (ctx.state === 'suspended') { ctx.close(); return; }

    const now = ctx.currentTime;
    [[880, 0], [1320, 0.11]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.12, now + at + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.24);
    });
    setTimeout(() => ctx.close(), 800);
  } catch {
    /* audio is a nicety — never let it break the notification */
  }
};

export default function PushForeground() {
  const navigate = useNavigate();

  useEffect(() => {
    let unsubscribe;
    let cancelled = false;

    (async () => {
      try {
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
        const messaging = await getMessagingIfSupported();
        if (!messaging || cancelled) return;

        const { onMessage } = await import(
          /* webpackChunkName: "firebase-messaging" */ 'firebase/messaging'
        );
        unsubscribe = onMessage(messaging, (payload) => {
          const { title, body } = payload.notification || {};
          const link = payload.data?.link;
          const pushId = payload.data?.push_id;
          // A foreground push is every bit as "received" as a background one —
          // counting only the service-worker path would make anyone who keeps
          // the dashboard open look like they never get notified.
          ackPush(pushId, 'received');
          playChime();
          toast(
            (t) => (
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t.id);
                  ackPush(pushId, 'clicked');
                  if (link) navigate(link);
                }}
                className="text-left"
              >
                <p className="text-sm font-bold text-gray-900">{title || 'MintLink'}</p>
                {body && <p className="text-xs text-gray-500 mt-0.5">{body}</p>}
              </button>
            ),
            { duration: 6000, icon: '🔔' },
          );
        });
      } catch (err) {
        console.error('[push] foreground listener failed', err);
      }
    })();

    return () => { cancelled = true; if (unsubscribe) unsubscribe(); };
  }, [navigate]);

  return null;
}
